import React, { useRef, useState, useCallback, useEffect } from "react";
import { useInView } from "framer-motion";
import { Box, type BoxProps, Button, type ButtonProps } from "@chakra-ui/react";
import { PauseIcon, PlayOutlineIcon } from "./icons";

type AnimationVideoProps = {
  webm?: string;
  mp4: string;
  poster: string;
  hideControls?: boolean;
  isLooped?: boolean;
  controlsColor?: BoxProps["color"];
  viewportAmount?: number;
};

export function AnimationVideo({
  webm,
  mp4,
  poster,
  hideControls,
  isLooped,
  controlsColor,
  viewportAmount = 0.7,
  ...props
}: AnimationVideoProps & BoxProps) {
  const video = useRef<HTMLVideoElement>(null);
  const isInView = useInView(video, { amount: viewportAmount });
  const [play, setPlay] = useState(false);

  const playAnimation = useCallback(() => {
    const playPromise = video.current?.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          setPlay(true);
        })
        .catch(() => {
          setPlay(false);
        });
    }
  }, []);

  const pauseAnimation = useCallback(() => {
    video.current?.pause();
    setPlay(false);
  }, []);

  const handleVideoEnd = () => {
    if (!isLooped) {
      setPlay(false);
    }
  };

  useEffect(() => {
    // Pause an animation when user is not looking
    if (isInView) {
      playAnimation();
    } else {
      pauseAnimation();
    }
  }, [isInView, playAnimation, pauseAnimation]);

  return (
    <Box pos="relative" overflow="hidden" {...props}>
      <video
        ref={video}
        playsInline
        muted
        poster={poster}
        width="100%"
        preload="auto"
        loop={isLooped}
        onEnded={handleVideoEnd}
      >
        {webm && <source src={webm} type="video/webm" />}
        <source src={mp4} type="video/mp4" />
      </video>

      {!hideControls &&
        (play ? (
          <ControllerButton
            color={controlsColor}
            aria-label="Pause animation."
            onClick={() => {
              pauseAnimation();
            }}
          >
            <PauseIcon boxSize={6} />
          </ControllerButton>
        ) : (
          <ControllerButton
            color={controlsColor}
            aria-label="Play animation."
            onClick={() => {
              playAnimation();
            }}
          >
            <PlayOutlineIcon boxSize={6} />
          </ControllerButton>
        ))}
    </Box>
  );
}

function ControllerButton({ children, color = "brand.textSecondary.light", ...props }: ButtonProps) {
  return (
    <Button
      variant="unstyled"
      minW="0px"
      pos="absolute"
      bottom="smallest"
      right="smallest"
      fontSize="sm"
      color={color}
      {...props}
    >
      {children}
    </Button>
  );
}
